import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { MainLayout } from "./components/Layout/MainLayout";
import { Toaster } from "./components/ui/sonner";
import { registerServiceWorker, PWAInstallBanner } from "./lib/pwa";
import { SubscriptionExpiredOverlay } from "./components/SubscriptionExpiredOverlay";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Properties from "./pages/Properties";
import Clients from "./pages/Clients";
import Transactions from "./pages/Transactions";
import RentPayments from "./pages/RentPayments";
import RentIncreases from "./pages/RentIncreases";
import DueNotices from "./pages/DueNotices";
import AllReceipts from "./pages/AllReceipts";
import ActivityLogs from "./pages/ActivityLogs";
import AgencySettings from "./pages/AgencySettings";
import TenantAccounts from "./pages/TenantAccounts";
import TenantRequests from "./pages/TenantRequests";
import UserManagement from "./pages/UserManagement";
import TenantLogin from "./pages/TenantLogin";
import TenantDashboard from "./pages/TenantDashboard";
import VerifyAttestation from "./pages/VerifyAttestation";
import VerifyParcel from "./pages/VerifyParcel";
import Shareholders from "./pages/Shareholders";
import GeneralAssemblies from "./pages/GeneralAssemblies";
import FinancialStatements from "./pages/FinancialStatements";
import OwnerBalance from "./pages/OwnerBalance";
import SecurityDeposits from "./pages/SecurityDeposits";
import LandSales from "./pages/LandSales";
import PropertyInspections from "./pages/PropertyInspections";
import PropertyVisits from "./pages/PropertyVisits";
import KeyHandovers from "./pages/KeyHandovers";
import Repairs from "./pages/Repairs";
import RepairRequests from "./pages/RepairRequests";
import AgencyExpenses from "./pages/AgencyExpenses";
import MySubscription from "./pages/MySubscription";
import AdminMobilePayments from "./pages/AdminMobilePayments";
import AgencyBalance from "./pages/AgencyBalance";
import AgencyStats from "./pages/AgencyStats";
import AgencyRegister from "./pages/AgencyRegister";
import AgenciesAdmin from "./pages/AgenciesAdmin";
import OwnerLogin from "./pages/OwnerLogin";
import OwnerDashboard from "./pages/OwnerDashboard";
import OwnerAccess from "./pages/OwnerAccess";
import DocumentTemplates from "./pages/DocumentTemplates";
import Tasks from "./pages/Tasks";
import ConfidentialityAdmin from "./pages/ConfidentialityAdmin";
import MyHR from "./pages/MyHR";
import StaffManagement from "./pages/StaffManagement";
import TenantNotices from "./pages/TenantNotices";
import TenantSituations from "./pages/TenantSituations";
import AgentPermissions from "./pages/AgentPermissions";
import AgentDeposits from "./pages/AgentDeposits";
import QrCodes from "./pages/QrCodes";
import Construction from "./pages/Construction";
import Chat from "./pages/Chat";
import Training from "./pages/Training";
import ParcelVerifications from "./pages/ParcelVerifications";
import PrintableForms from "./pages/PrintableForms";
import MarketingContracts from "./pages/MarketingContracts";
import Shipments from "./pages/Shipments";
import Partnerships from "./pages/Partnerships";
import PublicTracking from "./pages/PublicTracking";
import MandateSign from "./pages/MandateSign";
import MoratoriumSign from "./pages/MoratoriumSign";
import SignDocument from "./pages/SignDocument";
import Moratoriums from "./pages/Moratoriums";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route wrapper (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="spinner" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      
      {/* Agency Registration (public) */}
      <Route path="/agency-register" element={<AgencyRegister />} />
      
      {/* Owner Portal (public) */}
      <Route path="/owner/login" element={<OwnerLogin />} />
      <Route path="/owner/dashboard" element={<OwnerDashboard />} />
      
      {/* Tenant Portal routes */}
      <Route path="/tenant/login" element={<TenantLogin />} />
      <Route path="/tenant/dashboard" element={<TenantDashboard />} />

      {/* Public verification routes (no auth) */}
      <Route path="/verify/attestation/:id" element={<VerifyAttestation />} />
      <Route path="/verify/parcel/:id" element={<VerifyParcel />} />
      <Route path="/track/:tracking_number" element={<PublicTracking />} />
      <Route path="/mandate/sign/:token" element={<MandateSign />} />
      <Route path="/moratorium/sign/:token" element={<MoratoriumSign />} />
      <Route path="/sign/:token" element={<SignDocument />} />
      
      {/* Protected routes */}
      <Route element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route path="/" element={<Dashboard />} />
        <Route path="/properties" element={<Properties />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/transactions" element={<Transactions />} />
        <Route path="/rent-payments" element={<RentPayments />} />
        <Route path="/moratoriums" element={<Moratoriums />} />
        <Route path="/rent-increases" element={<RentIncreases />} />
        <Route path="/due-notices" element={<DueNotices />} />
        <Route path="/all-receipts" element={<AllReceipts />} />
        <Route path="/financial-statements" element={<FinancialStatements />} />
        <Route path="/owner-balance" element={<OwnerBalance />} />
        <Route path="/agency-balance" element={<AgencyBalance />} />
        <Route path="/agency-stats" element={<AgencyStats />} />
        <Route path="/security-deposits" element={<SecurityDeposits />} />
        <Route path="/land-sales" element={<LandSales />} />
        <Route path="/property-inspections" element={<PropertyInspections />} />
        <Route path="/property-visits" element={<PropertyVisits />} />
        <Route path="/key-handovers" element={<KeyHandovers />} />
        <Route path="/repairs" element={<Repairs />} />
        <Route path="/repair-requests" element={<RepairRequests />} />
        <Route path="/parcel-verifications" element={<ParcelVerifications />} />
        <Route path="/printable-forms" element={<PrintableForms />} />
        <Route path="/internship-certificates" element={<Navigate to="/staff-management?tab=internships" replace />} />
        <Route path="/marketing-contracts" element={<MarketingContracts />} />
        <Route path="/shipments" element={<Shipments />} />
        <Route path="/partnerships" element={<Partnerships />} />
        <Route path="/agency-expenses" element={<AgencyExpenses />} />
        <Route path="/my-subscription" element={<MySubscription />} />
        <Route path="/admin/mobile-payments" element={<AdminMobilePayments />} />
        <Route path="/tenant-accounts" element={<TenantAccounts />} />
        <Route path="/tenant-requests" element={<TenantRequests />} />
        <Route path="/users" element={<UserManagement />} />
        <Route path="/activity-logs" element={<ActivityLogs />} />
        <Route path="/agency-settings" element={<AgencySettings />} />
        <Route path="/agencies-admin" element={<AgenciesAdmin />} />
        <Route path="/owner-access" element={<OwnerAccess />} />
        <Route path="/document-templates" element={<DocumentTemplates />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/confidentiality" element={<ConfidentialityAdmin />} />
        <Route path="/time-tracking" element={<Navigate to="/staff-management?tab=time" replace />} />
        <Route path="/payslips" element={<Navigate to="/staff-management?tab=payslips" replace />} />
        <Route path="/staff-management" element={<StaffManagement />} />
        <Route path="/my-hr" element={<MyHR />} />
        <Route path="/tenant-notices" element={<TenantNotices />} />
        <Route path="/tenant-situations" element={<TenantSituations />} />
        <Route path="/shareholders" element={<Shareholders />} />
        <Route path="/general-assemblies" element={<GeneralAssemblies />} />
        <Route path="/agent-permissions" element={<AgentPermissions />} />
        <Route path="/agent-deposits" element={<AgentDeposits />} />
        <Route path="/qr-codes" element={<QrCodes />} />
        <Route path="/construction" element={<Construction />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/training" element={<Training />} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Register service worker on app load
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <PWAInstallBanner />
          <SubscriptionExpiredOverlay />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
